import { create } from 'zustand';
import {
  marketResearchApi,
  type FoundationQuerySummary,
  type KeywordIntent,
  type ScenarioBrowseResponse,
  type ScenarioDetail,
  type ScenarioSummary,
} from '@/services/api';

interface Filters {
  search: string;
  purchaseIntent: string;
  decisionComplexity: string;
}

interface ScenariosState {
  // Selection
  clientId: string | null;

  // List view
  scenarios: ScenarioSummary[];
  totalCount: number;
  facets: ScenarioBrowseResponse['facets'];
  listLoading: boolean;
  listError: string | null;

  // Filters
  filters: Filters;

  // Detail pane
  selectedScenarioId: string | null;
  detail: ScenarioDetail | null;
  detailLoading: boolean;
  detailError: string | null;

  // Linked keyword intents
  keywordIntents: KeywordIntent[];
  keywordIntentsLoading: boolean;
  keywordIntentsError: string | null;

  // Linked foundation model queries
  foundationQueries: FoundationQuerySummary[];
  foundationQueriesLoading: boolean;
  foundationQueriesError: string | null;

  // Actions
  setClient: (clientId: string | null) => Promise<void>;
  setFilter: (key: keyof Filters, value: string) => void;
  clearFilters: () => void;
  fetchScenarios: () => Promise<void>;
  selectScenario: (scenarioId: string | null) => Promise<void>;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  purchaseIntent: '',
  decisionComplexity: '',
};

export const useScenariosStore = create<ScenariosState>((set, get) => ({
  clientId: null,

  scenarios: [],
  totalCount: 0,
  facets: {},
  listLoading: false,
  listError: null,

  filters: { ...DEFAULT_FILTERS },

  selectedScenarioId: null,
  detail: null,
  detailLoading: false,
  detailError: null,

  keywordIntents: [],
  keywordIntentsLoading: false,
  keywordIntentsError: null,

  foundationQueries: [],
  foundationQueriesLoading: false,
  foundationQueriesError: null,

  setClient: async (clientId) => {
    set({
      clientId,
      scenarios: [],
      totalCount: 0,
      facets: {},
      selectedScenarioId: null,
      detail: null,
      keywordIntents: [],
      foundationQueries: [],
      listError: null,
      detailError: null,
      keywordIntentsError: null,
      foundationQueriesError: null,
    });
    if (clientId) {
      await get().fetchScenarios();
    }
  },

  setFilter: (key, value) => {
    set((s) => ({ filters: { ...s.filters, [key]: value } }));
    // Debounce-free: trigger fetch immediately (the server-side pool is bounded).
    void get().fetchScenarios();
  },

  clearFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS } });
    void get().fetchScenarios();
  },

  fetchScenarios: async () => {
    const { clientId, filters } = get();
    if (!clientId) return;
    set({ listLoading: true, listError: null });
    try {
      const res = await marketResearchApi.browseScenarios({
        clientId,
        search: filters.search || undefined,
        purchaseIntent: filters.purchaseIntent || undefined,
        decisionComplexity: filters.decisionComplexity || undefined,
        limit: 300,
      });
      set({
        scenarios: res.scenarios,
        totalCount: res.total_count,
        facets: res.facets ?? {},
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load scenarios';
      set({ listError: msg });
    } finally {
      set({ listLoading: false });
    }
  },

  selectScenario: async (scenarioId) => {
    set({
      selectedScenarioId: scenarioId,
      detail: null,
      keywordIntents: [],
      foundationQueries: [],
      detailError: null,
      keywordIntentsError: null,
      foundationQueriesError: null,
    });
    if (!scenarioId) return;
    const { clientId } = get();

    set({
      detailLoading: true,
      keywordIntentsLoading: true,
      foundationQueriesLoading: true,
    });

    const [detailRes, kiRes, fmqRes] = await Promise.allSettled([
      marketResearchApi.getScenario(scenarioId),
      marketResearchApi.getScenarioKeywordIntents(
        scenarioId,
        clientId ?? undefined,
      ),
      marketResearchApi.getScenarioFoundationQueries(
        scenarioId,
        clientId ?? undefined,
      ),
    ]);

    if (detailRes.status === 'fulfilled') {
      set({ detail: detailRes.value });
    } else {
      set({
        detailError:
          detailRes.reason instanceof Error
            ? detailRes.reason.message
            : 'Failed to load scenario',
      });
    }
    set({ detailLoading: false });

    if (kiRes.status === 'fulfilled') {
      set({ keywordIntents: kiRes.value.keyword_intents });
    } else {
      set({
        keywordIntentsError:
          kiRes.reason instanceof Error
            ? kiRes.reason.message
            : 'Failed to load keyword intents',
      });
    }
    set({ keywordIntentsLoading: false });

    if (fmqRes.status === 'fulfilled') {
      set({ foundationQueries: fmqRes.value.queries });
    } else {
      set({
        foundationQueriesError:
          fmqRes.reason instanceof Error
            ? fmqRes.reason.message
            : 'Failed to load foundation queries',
      });
    }
    set({ foundationQueriesLoading: false });
  },
}));
